import { Component, EventEmitter, OnDestroy, OnInit, Output,
  ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ExamsRolloverService } from './exams-rollover.service';

@Component({
    selector: 'gtw-exams-rollover',
    templateUrl: './exams-rollover.component.html',
    styleUrls: ['./exams-rollover.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExamsRolloverComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private initialized = false;

  logged_in_user = '';

  filteredTaxYearData: any[] = [];
  scenarioRAF: any[] = [];
  scenarioTargetRAF: any[] = [];

  selectedTaxYear = '';
  selectedScenario = '';
  selectedTargetScenario = '';

  baseURLs: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private examsRolloverService: ExamsRolloverService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  private jsonGlobalContext(): any {
    return { globalParams: this.globalService };
  }

  private init(): void {
    if (this.initialized) return;
    const baseUrl = this.getBaseUrl();
    if (!baseUrl || !this.globalService || Object.keys(this.globalService).length === 0) return;

    this.initialized = true;
    this.logged_in_user =
      this.USER_SETTINGS?.user?.sso_id ||
      this.globalService?.sso_id ||
      '';
    this.getTaxYearDropDown();
  }

  private getBaseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ||
      this.baseURLs?.base_url ||
      this.globalService?.base_url ||
      '';
  }

  getTaxYearDropDown(): void {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) return;

    this.selectedTaxYear = '';
    const currentTaxYear =
      parseInt(this.globalService?.tax_year, 10) ||
      parseInt(this.filterParams?.tax_year, 10) ||
      new Date().getFullYear();

    this.examsRolloverService
      .getTaxYearDropDown(baseUrl, this.jsonGlobalContext()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          const data = response?.jsonObject || [];
          const taxYearData = _.filter(data, (p: any) => _.includes(['TAX_YEAR'], p.CODE_GROUP));

          this.filteredTaxYearData = [];
          for (const item of taxYearData) {
            const itemYear = parseInt(item.CODE_DISP_DESC, 10);
            if (item.CODE_GROUP === 'TAX_YEAR' && itemYear <= currentTaxYear && itemYear >= currentTaxYear - 5) {
              this.filteredTaxYearData.push(item);
            }
          }
          this.filteredTaxYearData = _.orderBy(this.filteredTaxYearData, ['CODE_DISP_DESC'], ['desc']);
          this.cdr.detectChanges();
        },
        () => this.cdr.detectChanges(),
      );
  }

  onTaxYearChange(): void {
    this.scenarioRAF = [];
    this.scenarioTargetRAF = [];
    this.selectedScenario = '';
    this.selectedTargetScenario = '';
    this.cdr.detectChanges();
    this.getScenarioDropDown();
  }

  getScenarioDropDown(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select the Tax Year to get the Scenario.', 'gtw-snackbar--red');
      return;
    }

    const baseUrl = this.getBaseUrl();
    if (!baseUrl) return;

    this.examsRolloverService
      .getScenarioDropDown(baseUrl, this.selectedTaxYear, this.jsonGlobalContext()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          const scenarioDropDown = response?.jsonObject || [];

          this.scenarioRAF = scenarioDropDown;
          this.scenarioTargetRAF = _.filter(scenarioDropDown, (p: any) => !_.includes(['Return As Filed'], p.LABEL));

          this.selectedScenario =
            this.scenarioRAF.length > 0 ? this.scenarioRAF[0].VALUE : '';
          this.selectedTargetScenario =
            this.scenarioTargetRAF.length > 0 ? this.scenarioTargetRAF[0].VALUE : '';

          this.cdr.detectChanges();
        },
        () => {
          this.scenarioRAF = [];
          this.scenarioTargetRAF = [];
          this.selectedScenario = '';
          this.selectedTargetScenario = '';
          this.cdr.detectChanges();
        },
      );
  }

  confirmRunExamsRollover(): void {
    this.confirmAction(
      'Run Exams Rollover',
      'Are you sure to Run the Exams Rollover process?',
      () => this.runExamsRolloverProcess(),
    );
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.detectChanges();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.detectChanges();
  }

  runExamsRolloverProcess(): void {
    if (!this.selectedTaxYear) {
      this.gtwSnackbarService.openSnackBar('Please select the Tax Year to run the Apportionment.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedScenario) {
      this.gtwSnackbarService.openSnackBar('Please select the Source Scenario to run the rollover process.', 'gtw-snackbar--red');
      return;
    }
    if (!this.selectedTargetScenario) {
      this.gtwSnackbarService.openSnackBar('Please select the Target Scenario to run the rollover process.', 'gtw-snackbar--red');
      return;
    }

    const baseUrl = this.getBaseUrl();
    if (!baseUrl) return;

    const jcdKey = this.filterParams?.jcd_key || this.globalService?.jcd_key;

    this.examsRolloverService
      .executeExamsRollover(
        baseUrl,
        this.logged_in_user,
        this.selectedTaxYear,
        this.selectedScenario,
        this.selectedTargetScenario,
        jcdKey,
        this.jsonGlobalContext(),
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: any) => {
          const response = result.data || result;
          const errorMessage = response.errorMessage || result.errorMessage;

          if (errorMessage && errorMessage !== 'null' && errorMessage !== '') {
            this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar(
              'For the selection made, the process to run the rollover is triggered.',
              'gtw-snackbar--green',
            );
          }
          this.cdr.detectChanges();
        },
        () => {
          this.gtwSnackbarService.openSnackBar('Error occurred while running rollover. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      );
  }

  reset(): void {
    this.selectedTaxYear = '';
    this.scenarioRAF = [];
    this.scenarioTargetRAF = [];
    this.selectedScenario = '';
    this.selectedTargetScenario = '';
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
